import React from 'react'
import { listAnalyses, type AnalysisItem } from '../services/analysisService'

function mergeUniqueById(prev: AnalysisItem[], next: AnalysisItem[]): AnalysisItem[] {
  if (!prev.length) return next
  if (!next.length) return prev

  const merged = [...prev]
  const indexById = new Map<number, number>()

  merged.forEach((it, idx) => {
    indexById.set(Number(it.id), idx)
  })

  next.forEach((it) => {
    const id = Number(it.id)
    const existingIndex = indexById.get(id)
    if (existingIndex === undefined) {
      indexById.set(id, merged.length)
      merged.push(it)
      return
    }
    merged[existingIndex] = it
  })

  return merged
}

export function useAnalysisList(pageSize = 25) {
  const [items, setItems] = React.useState<AnalysisItem[]>([])
  const [page, setPage] = React.useState(1)
  const [loading, setLoading] = React.useState(false)
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [hasMore, setHasMore] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const isFetchingRef = React.useRef(false)

  const loadPage = React.useCallback(
    async (nextPage: number, append: boolean) => {
      if (isFetchingRef.current) return
      isFetchingRef.current = true

      if (!append) {
        setLoading(true)
      } else {
        setLoadingMore(true)
      }
      setError(null)
      try {
        const { items: pageItems } = await listAnalyses({ page: nextPage, pageSize })
        setPage(nextPage)
        setHasMore(pageItems.length === pageSize)
        setItems((prev) => (append ? mergeUniqueById(prev, pageItems) : pageItems))
      } catch (e: any) {
        setError(e?.message || 'Falha ao carregar análises')
      } finally {
        isFetchingRef.current = false
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [pageSize],
  )

  React.useEffect(() => {
    // primeira carga
    loadPage(1, false)
  }, [loadPage])

  function reload() {
    loadPage(1, false)
  }

  function loadMore() {
    if (!hasMore) return
    loadPage(page + 1, true)
  }

  return {
    items,
    page,
    loading,
    loadingMore,
    hasMore,
    error,
    reload,
    loadMore,
  }
}
